import { Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/menu';
import { IconButton, Portal } from '@chakra-ui/react';
import { IconArrowInbox } from 'central-icons/IconArrowInbox';
import { IconChainLink4 } from 'central-icons/IconChainLink4';
import { IconChart2 } from 'central-icons/IconChart2';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import { IconExpand45 } from 'central-icons/IconExpand45';
import { IconImageSparkle } from 'central-icons/IconImageSparkle';
import { IconWindowCursor } from 'central-icons/IconWindowCursor';

import { FeatureFlag } from '@revelio/core';

import { MenuItemWithDisabledReason } from '../../../../../shared/ui/menu-item/MenuItemWithDisabledReason';
import { useUnleashFlag } from '../../../../../shared/unleash-flags/use-unleash-flag';
import styles from './ChartActionMenu.module.css';

export type MenuOptions =
  | 'expand-plot'
  | 'share-plot'
  | 'download-data'
  | 'download-png'
  | 'download-ppt';

export const ChartActionMenuDropdown = ({
  menu,
  slim,
  notSharableReason,
  downloadDataDisabled,
  downloadPNGDisabled,
  downloadPPTDisabled,
  previewPPTDisabled,
  onExpandPlotClick,
  onSharePlotClick,
  onDownloadDataClick,
  onDownloadPNGClick,
  onDownloadPPTClick,
  onPreviewPPTClick,
}: {
  slim?: boolean;
  menu: MenuOptions[];
  notSharableReason: string | null;
  downloadDataDisabled?: boolean;
  downloadPNGDisabled?: boolean;
  downloadPPTDisabled?: boolean;
  previewPPTDisabled?: boolean;
  onExpandPlotClick: () => void;
  onSharePlotClick: () => void;
  onDownloadDataClick: () => void;
  onDownloadPNGClick: () => void;
  onDownloadPPTClick: () => void;
  onPreviewPPTClick: () => void;
}) => {
  const isPPTPreviewEnabled = useUnleashFlag(FeatureFlag.PPTPreview);

  return (
    <Menu placement="bottom-end">
      {({ isOpen }) => (
        <>
          <MenuButton
            as={IconButton}
            size="xs"
            {...(slim && { boxSize: '14px' })}
            marginLeft="4px"
            width="16px"
            minWidth="16px"
            borderRadius="4px"
            variant="ghost"
            colorScheme="gray"
            icon={<IconDotGrid1x3Vertical size={14} />}
            data-testid="action-menu-button"
            className={`${styles.menuButton}`}
            opacity={isOpen ? 1 : 0}
            _groupHover={{ opacity: 1 }}
          />
          {isOpen && (
            <Portal>
              <MenuList minWidth="10rem" data-testid="action-menu-list">
                {menu.includes('expand-plot') && (
                  <MenuItem
                    className={styles.menuItem}
                    icon={<IconExpand45 size={14} />}
                    onClick={onExpandPlotClick}
                  >
                    Expand Plot
                  </MenuItem>
                )}
                {menu.includes('share-plot') && (
                  <MenuItemWithDisabledReason
                    className={styles.menuItem}
                    icon={<IconChainLink4 size={14} />}
                    onClick={onSharePlotClick}
                    isDisabled={notSharableReason !== null}
                    disabledReason={notSharableReason ?? ''}
                  >
                    Share Plot
                  </MenuItemWithDisabledReason>
                )}
                {menu.includes('download-data') && (
                  <MenuItemWithDisabledReason
                    className={styles.menuItem}
                    icon={<IconArrowInbox size={14} />}
                    onClick={onDownloadDataClick}
                    isDisabled={downloadDataDisabled}
                    disabledReason="Available on paid plans"
                  >
                    Download Data
                  </MenuItemWithDisabledReason>
                )}
                {menu.includes('download-png') && (
                  <MenuItemWithDisabledReason
                    className={styles.menuItem}
                    icon={<IconImageSparkle size={14} />}
                    onClick={onDownloadPNGClick}
                    isDisabled={downloadPNGDisabled}
                    disabledReason="Available on paid plans"
                  >
                    Download PNG
                  </MenuItemWithDisabledReason>
                )}
                {menu.includes('download-ppt') && (
                  <>
                    <MenuItemWithDisabledReason
                      className={styles.menuItem}
                      icon={<IconChart2 size={14} />}
                      onClick={onDownloadPPTClick}
                      isDisabled={downloadPPTDisabled}
                      disabledReason="Available on paid plans"
                    >
                      Download PPT
                    </MenuItemWithDisabledReason>
                    {isPPTPreviewEnabled && (
                      <MenuItemWithDisabledReason
                        className={styles.menuItem}
                        icon={<IconWindowCursor size={14} />}
                        onClick={onPreviewPPTClick}
                        isDisabled={previewPPTDisabled}
                        disabledReason="Available on paid plans"
                      >
                        Preview PPT
                      </MenuItemWithDisabledReason>
                    )}
                  </>
                )}
              </MenuList>
            </Portal>
          )}
        </>
      )}
    </Menu>
  );
};
